// Moved from `src/hooks/mutation/useMutateTeamShortcut.ts` (now the app
// delegate) with the org-teams D2 residue (ARCFE-1062). This was stayer 3 of
// the four OT4 left behind (ARCFE-1033); its D1 half — the uncarved
// `src/lib/api/team-shortcuts.ts` transport client — closed at ARCFE-1041, which
// carved `../api/team-shortcuts.ts`.
//
// WHAT REMAINED, AND HOW EACH HALF RESOLVED:
//   - `invalidateEvidenceConfig()` is EVIDENCE's command and stays app-side
//     PERMANENTLY, not pending an evidence carve: `workspace` is the sole
//     FOUNDATIONAL domain, so it can never take a product-domain value import —
//     probed and measured on this family's blocker and in
//     `useMutateOrganizationById.ts`. It crosses as `onShortcutsChanged`, fired
//     in each of the three positions the legacy call held.
//   - The `GET_TEAM_SHORTCUTS` key factory was app-side in
//     `src/hooks/query/useQueryTeamShortcuts.ts`. It moved to
//     `@heidi/workspace/contracts` (that file now re-exports both the constant
//     and the factory) — the `GET_TEAM_MEMBERS_WITH_RELATIONSHIP` precedent from
//     OT4, and required here because all three hooks read, write AND invalidate
//     against the identity.
//
// `invalidateTeamShortcuts(teamId)` is not a port: it was a bare
// `reactQueryClient.invalidateQueries` on that same workspace key with no
// swallow and no second leg, and `useQueryClient()` resolves to the very same
// client instance the app seeds its provider with. Reproducing it here keeps a
// workspace-owned invalidation inside workspace, unlike the COMPOSITE
// `invalidateCurrentUser()` on the identity siblings.
import { logger } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getTeamShortcutsQueryKey,
  type CreateTeamShortcutRequest,
  type TeamShortcut,
  type UpdateTeamShortcutRequest,
} from '../../contracts';
import { teamShortcutsApi } from '../api/team-shortcuts';

export interface UseMutateTeamShortcutOptions {
  /**
   * App-reaction port: fired where the EVIDENCE `invalidateEvidenceConfig()`
   * call fired — LAST, after the team-shortcut list invalidation, in each of
   * create's `onSuccess` and update/delete's `onSettled`. The member-facing
   * `/transcript-chips/config` merges these shortcuts, so the chip rail needs
   * the refresh.
   */
  onShortcutsChanged?: () => void;
}

/**
 * Create a team shortcut. On success invalidates the team list and (through the
 * delegate's port) the member-facing /transcript-chips/config so the new chip
 * appears in the chip rail for everyone.
 */
export function useMutateCreateTeamShortcut(
  teamId: string | undefined,
  options?: UseMutateTeamShortcutOptions
) {
  const queryClient = useQueryClient();

  return useMutation<TeamShortcut, Error, CreateTeamShortcutRequest>({
    mutationFn: (body) => {
      if (!teamId) throw new Error('teamId is required');
      return teamShortcutsApi.create(teamId, body);
    },
    onSuccess: (created) => {
      queryClient.setQueryData<TeamShortcut[]>(
        getTeamShortcutsQueryKey(teamId),
        (prev) => (prev ? [created, ...prev] : [created])
      );
      void queryClient.invalidateQueries({
        queryKey: getTeamShortcutsQueryKey(teamId),
      });
      options?.onShortcutsChanged?.();
    },
    onError: (error) => {
      logger.error('Failed to create team shortcut:', error);
    },
  });
}

/**
 * Update a team shortcut (partial). Uses optimistic list update keyed by id.
 */
export function useMutateUpdateTeamShortcut(
  teamId: string | undefined,
  options?: UseMutateTeamShortcutOptions
) {
  const queryClient = useQueryClient();

  return useMutation<
    TeamShortcut,
    Error,
    { shortcutId: string; patch: UpdateTeamShortcutRequest },
    { previous: TeamShortcut[] | undefined }
  >({
    mutationFn: ({ shortcutId, patch }) => {
      if (!teamId) throw new Error('teamId is required');
      return teamShortcutsApi.update(teamId, shortcutId, patch);
    },
    onMutate: async ({ shortcutId, patch }) => {
      const key = getTeamShortcutsQueryKey(teamId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TeamShortcut[]>(key);
      queryClient.setQueryData<TeamShortcut[]>(key, (prev) =>
        prev?.map((s) => (s.id === shortcutId ? { ...s, ...patch } : s))
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      logger.error('Failed to update team shortcut:', error);
      if (context?.previous) {
        queryClient.setQueryData(getTeamShortcutsQueryKey(teamId), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: getTeamShortcutsQueryKey(teamId),
      });
      options?.onShortcutsChanged?.();
    },
  });
}

/**
 * Soft-delete a team shortcut. Optimistically removes from list.
 */
export function useMutateDeleteTeamShortcut(
  teamId: string | undefined,
  options?: UseMutateTeamShortcutOptions
) {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { shortcutId: string },
    { previous: TeamShortcut[] | undefined }
  >({
    mutationFn: ({ shortcutId }) => {
      if (!teamId) throw new Error('teamId is required');
      return teamShortcutsApi.delete(teamId, shortcutId);
    },
    onMutate: async ({ shortcutId }) => {
      const key = getTeamShortcutsQueryKey(teamId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TeamShortcut[]>(key);
      queryClient.setQueryData<TeamShortcut[]>(key, (prev) =>
        prev?.filter((s) => s.id !== shortcutId)
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      logger.error('Failed to delete team shortcut:', error);
      if (context?.previous) {
        queryClient.setQueryData(getTeamShortcutsQueryKey(teamId), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: getTeamShortcutsQueryKey(teamId),
      });
      options?.onShortcutsChanged?.();
    },
  });
}
